"use client";

import React from "react";
import { Car, Gauge, Settings, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const FLEET_DATA = [{
  title: "Executive Sedan",
  subtitle: "Mercedes-Benz S-Class or similar",
  description: "The gold standard of luxury travel. Perfect for high-profile airport transfers and corporate travel with unmatched refinement.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/executive_sedan_fleet.png",
  category: "Business",
  price: "95"
}, {
  title: "Luxury SUV",
  subtitle: "Cadillac Escalade or similar",
  description: "Unmatched presence and space. Ideal for groups of up to 6 passengers with significant luggage and superior comfort.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/luxury_suv_fleet.png",
  category: "Group",
  price: "125"
}, {
  title: "First Class",
  subtitle: "BMW 7 Series or similar",
  description: "The ultimate in rear-seat comfort. Features extended legroom, privacy shades, and premium climate control for the discerning traveler.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/first_class_fleet.png",
  category: "Elite",
  price: "150"
}];
function FahrzeugeGrid() {
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {FLEET_DATA.map((item, i) => <Card key={i} data-index={i} className="group flex flex-col overflow-hidden bg-[#121212] backdrop-blur-md border-t-2 border-t-primary/40 border-x-0 border-b-0 shadow-2xl hover:shadow-primary/10 transition-all duration-500">
          <div className="block relative overflow-hidden">
            <AspectRatio ratio={16 / 10}>
              <img src={item.image} alt={item.title} className="object-cover w-full h-full grayscale-[0.3] contrast-125 transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" loading="lazy" />
            </AspectRatio>
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-80" />
            
            <div className="absolute top-4 left-4 flex gap-2 flex-wrap z-10">
              <Badge variant="secondary" className="bg-primary/90 backdrop-blur-sm text-primary-foreground border-none shadow-lg font-sans text-[10px] uppercase tracking-widest">
                {item.category}
              </Badge>
            </div>
          </div>

          <CardHeader className="pb-4 relative z-10">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <CardTitle className="font-serif text-2xl tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                  {item.title}
                </CardTitle>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
                  {item.subtitle}
                </p>
              </div>
              <div className="text-right shrink-0">
                <span className="block text-xl font-semibold text-primary">
                  from ${item.price}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
                  per hour
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-grow pb-6">
            <div className="grid grid-cols-2 gap-y-4 gap-x-4 text-xs text-muted-foreground mb-6 font-sans">
              <div className="flex items-center gap-2.5">
                <Settings className="w-4 h-4 text-primary/60" />
                <span className="uppercase tracking-wider">Chauffeur</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Users className="w-4 h-4 text-primary/60" />
                <span className="uppercase tracking-wider">Executive</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Gauge className="w-4 h-4 text-primary/60" />
                <span className="uppercase tracking-wider">On-Time</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Car className="w-4 h-4 text-primary/60" />
                <span className="uppercase tracking-wider">Discretion</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground/80 line-clamp-3 leading-relaxed font-sans italic">
              {item.description}
            </p>
          </CardContent>

          <CardFooter className="pt-0 mt-auto">
            <Link to="#" className="w-full inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 font-sans font-bold uppercase tracking-widest text-[10px] py-6 transition-all duration-300 shadow-xl rounded-md">
              Explore Interior
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function Fahrzeuge() {
  return <>
      <div id="fleet" />
      <section id="executive-fleet" className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/image_6iqiirzz.png)] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/85 backdrop-blur-[2px]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-primary/5 blur-[150px] rounded-full pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter text-foreground mb-6">
              The Fleet
            </h2>
            <div className="w-20 h-[1px] bg-primary mx-auto mb-8 opacity-60" />
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-sans font-light tracking-wide">
              A curated selection of executive vehicles maintained to the highest standard. Every detail considered, every journey prepared.
            </p>
          </div>

          <FahrzeugeGrid />

          <div className="mt-12 text-center">
            <p className="text-xs text-muted-foreground/50 italic font-sans tracking-wide">
              Vehicle images are representative of class. Comparable vehicles may be provided based on availability.
            </p>
          </div>
        </div>
      </section>
    </>;
}